<script setup lang="ts">
import { onMounted, watch } from 'vue';
import BaseCard from '@/components/ui/BaseCard.vue';
import { useReportsStore } from '@/stores/reports';
import MonthPicker from '@/components/reports/MonthPicker.vue';
import GenerateReportButton from '@/components/reports/GenerateReportButton.vue';
import ReportListTable from '@/components/reports/ReportListTable.vue';
import ReportKpiGrid from '@/components/reports/ReportKpiGrid.vue';
import ReportCategoryBreakdown from '@/components/reports/ReportCategoryBreakdown.vue';
import ReportRepeatIssues from '@/components/reports/ReportRepeatIssues.vue';
import ReportResolutionTimes from '@/components/reports/ReportResolutionTimes.vue';
import ReportTrendChart from '@/components/reports/ReportTrendChart.vue';
import ReportErrorBanner from '@/components/reports/ReportErrorBanner.vue';

const store = useReportsStore();

onMounted(() => {
  void store.initReportsView();
});

// Re-fetch stats when the selected month changes (MonthPicker emits update).
watch(
  () => store.selectedMonth,
  (month) => {
    void store.fetchMonthlyStats(month);
  },
);

function handleGenerate(): void {
  void store.generateReport();
}

function handleDownload(id: string): void {
  void store.downloadReport(id);
}

function dismissError(): void {
  // Clear all errors — any subsequent action will surface a new error if it fails.
  store.error = null;
  store.generateError = null;
  store.downloadError = null;
}
</script>

<template>
  <div class="space-y-6 max-w-[1200px]" data-testid="reports-view">
    <!-- Page header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-navy-800">Monthly Board Reports</h1>
        <p class="text-sm text-muted mt-1">
          Generate, download, and review HOA Watch monthly PDF reports for board meetings.
        </p>
      </div>
      <GenerateReportButton
        :month="store.selectedMonth"
        :loading="store.generating"
        :has-existing="store.hasReportForSelectedMonth"
        @generate="handleGenerate"
      />
    </div>

    <!-- Error banners -->
    <ReportErrorBanner
      v-if="store.error || store.generateError || store.downloadError"
      :message="store.generateError ?? store.downloadError ?? store.error ?? ''"
      @dismiss="dismissError"
    />

    <!-- Month picker + current-month stats ──────────────────────────────── -->
    <div class="grid lg:grid-cols-3 gap-6">
      <!-- Left: month picker + KPI grid + category breakdown + repeats + resolution -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Month selector -->
        <BaseCard padding="md">
          <MonthPicker
            :model-value="store.selectedMonth"
            @update:model-value="(m) => void store.setSelectedMonth(m)"
          />
        </BaseCard>

        <!-- KPI headline numbers -->
        <BaseCard v-if="store.monthlyStats" padding="md">
          <template #header>
            <h2 class="text-sm font-semibold uppercase tracking-wide text-muted">
              Headline Metrics · {{ store.selectedMonth }}
            </h2>
          </template>
          <ReportKpiGrid
            v-if="!store.statsLoading"
            :kpis="store.monthlyStats.kpis"
          />
          <!-- Loading skeleton for KPI grid -->
          <div v-else class="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div v-for="i in 4" :key="i" class="h-20 bg-canvas-soft rounded animate-pulse" />
          </div>
        </BaseCard>

        <!-- Category breakdown -->
        <BaseCard v-if="store.monthlyStats && !store.statsLoading" padding="md">
          <template #header>
            <h2 class="text-sm font-semibold uppercase tracking-wide text-muted">
              Category Breakdown
            </h2>
          </template>
          <ReportCategoryBreakdown :categories="store.monthlyStats.byCategory" />
        </BaseCard>

        <!-- AI-detected repeat issues -->
        <BaseCard
          v-if="store.monthlyStats && !store.statsLoading"
          padding="md"
          class="border-l-4 border-l-teal-500"
        >
          <ReportRepeatIssues :repeats="store.monthlyStats.topRepeats" />
        </BaseCard>

        <!-- Resolution times -->
        <BaseCard v-if="store.monthlyStats && !store.statsLoading" padding="md">
          <template #header>
            <h2 class="text-sm font-semibold uppercase tracking-wide text-muted">
              Resolution Times (Median)
            </h2>
          </template>
          <ReportResolutionTimes :resolution="store.monthlyStats.resolutionTime" />
        </BaseCard>
      </div>

      <!-- Right: 6-month trend chart -->
      <div class="space-y-6">
        <BaseCard padding="md">
          <template #header>
            <h2 class="text-sm font-semibold uppercase tracking-wide text-muted">
              6-Month Trend
            </h2>
          </template>
          <div v-if="store.trendLoading" class="h-24 bg-canvas-soft rounded animate-pulse" />
          <ReportTrendChart
            v-else-if="store.trend"
            :months="store.trend.months"
          />
          <div v-else class="text-sm text-muted py-4">No trend data available.</div>
          <div v-if="store.trendError" class="text-xs text-red-600 mt-2">
            {{ store.trendError }}
          </div>
        </BaseCard>
      </div>
    </div>

    <!-- Past reports table ───────────────────────────────────────────────── -->
    <BaseCard padding="none">
      <template #header>
        <div class="px-4 pt-4">
          <h2 class="text-sm font-semibold uppercase tracking-wide text-muted">
            Past Reports
          </h2>
        </div>
      </template>
      <div v-if="store.loading" class="p-4 space-y-3">
        <div v-for="i in 3" :key="i" class="h-10 bg-canvas-soft rounded animate-pulse" />
      </div>
      <div v-else class="px-4 pb-4">
        <ReportListTable
          :reports="store.reports"
          :downloading="store.downloading"
          @download="handleDownload"
        />
      </div>
    </BaseCard>
  </div>
</template>
